import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";

import {
  nativeSheetBottomPad,
  nativeSheetScrollBottomPad,
} from "@/lib/native/nativeSheetPadding";
import { useNativeSheetSwipe } from "@/lib/native/useNativeSheetSwipe";
import {
  NativeSheetDismissProvider,
  useNativeSheetDismissContext,
} from "@/lib/native/nativeSheetDismissContext";
import { cleanupNativeSheetChrome } from "@/lib/native/nativeSheetCleanup";
import { isNativeParentSheetOpen } from "@/lib/native/nativeNestedParent";
import { useNativeSheetEnterStabilizer } from "@/lib/native/useNativeSheetEnterStabilizer";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { nativeMediaViewerZ, nativeOverlayZ, nativeSheetZ, webOverlayZ } from "@/lib/native/nativeZIndex";

/** Position lock only — never set translate here (breaks swipe-dismiss on transform layer). */
const nativeSheetLayoutLock =
  "!inset-x-0 !left-0 !right-0 !w-full !max-w-none !mx-0";

const DialogOpenContext = React.createContext(false);

const Dialog = ({
  onOpenChange,
  open: openProp,
  defaultOpen,
  beforeDismiss,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root> & {
  /** Return false to veto native swipe/backdrop dismiss. */
  beforeDismiss?: () => boolean;
}) => {
  const { isNative } = useNativePlatform();
  const isControlled = openProp !== undefined;
  const [internalOpen, setInternalOpen] = React.useState(
    () => openProp ?? defaultOpen ?? false,
  );
  const open = isControlled ? openProp : internalOpen;

  const finishClose = React.useCallback(() => {
    if (!isControlled) {
      setInternalOpen(false);
    }
    onOpenChange?.(false);
    if (isNative) {
      requestAnimationFrame(() => cleanupNativeSheetChrome());
    }
  }, [isControlled, onOpenChange, isNative]);

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (next) {
        if (!isControlled) {
          setInternalOpen(true);
        }
        onOpenChange?.(true);
        return;
      }
      if (!isNative) {
        if (!isControlled) {
          setInternalOpen(false);
        }
        onOpenChange?.(false);
      }
    },
    [isControlled, onOpenChange, isNative],
  );

  return (
    <NativeSheetDismissProvider
      onRequestClose={finishClose}
      beforeDismiss={beforeDismiss}
    >
      <DialogOpenContext.Provider value={open}>
        <DialogPrimitive.Root
          open={open}
          onOpenChange={handleOpenChange}
          {...props}
        />
      </DialogOpenContext.Provider>
    </NativeSheetDismissProvider>
  );
};

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  const { isNative } = useNativePlatform();
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        "fixed inset-0 h-dvh w-dvh",
        isNative
          ? `${nativeOverlayZ} bg-black/55`
          : `${webOverlayZ} bg-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0`,
        className,
      )}
      {...props}
    />
  );
});
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

interface DialogContentProps extends React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> {
  /** When false, hides the default close (X) button. Use when providing your own close control. Default true. */
  showCloseButton?: boolean;
  /** Native default is bottom sheet; use fullscreen for image lightbox etc. */
  presentation?: "sheet" | "fullscreen";
  /** Stack above another open native sheet (dims the sheet behind). */
  nested?: boolean;
  /** Nested sheet without dimming the parent (e.g. option picker). */
  nestedUndimmed?: boolean;
  /** Pass sheet open state to avoid enter-animation flash while typing (native). */
  sheetOpen?: boolean;
  /** Native sheet: swipe up to expand from default height to near-full screen. */
  expandable?: boolean;
  /** Native compact sheet: content height (pickers, small forms). Default when expandable is not true. */
  compact?: boolean;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, showCloseButton = true, presentation, nested = false, nestedUndimmed = false, sheetOpen, expandable = false, compact, ...props }, ref) => {
  const { isNative } = useNativePlatform();
  const dismissCtx = useNativeSheetDismissContext();
  const dialogOpen = React.useContext(DialogOpenContext);
  const effectiveSheetOpen = sheetOpen ?? dialogOpen;
  const enterStable = useNativeSheetEnterStabilizer(Boolean(effectiveSheetOpen));
  const isFullscreenNative =
    isNative && presentation === "fullscreen";
  const parentSheetOpen = isNative && isNativeParentSheetOpen();
  const effectiveNested = nested || parentSheetOpen;
  const nestedStack = effectiveNested || nestedUndimmed;
  const effectiveExpandable = expandable === true && isNative && !isFullscreenNative;
  const effectiveCompact = compact ?? (isNative && !isFullscreenNative && !effectiveExpandable);
  const sheetSwipe = useNativeSheetSwipe({
    enabled: isNative,
    dismissFromFullSheet: isFullscreenNative,
    expandable: effectiveExpandable,
    sheetOpen: effectiveSheetOpen,
  });
  const webNestedStack = !isNative && nestedStack;
  const nestedOverlayClass = nestedUndimmed
    ? "!bg-transparent !backdrop-blur-none pointer-events-none z-[230]"
    : effectiveNested
      ? "z-[230] bg-black/55"
      : undefined;
  const nestedSheetZ = nestedStack ? "z-[231]" : "z-[201]";

  if (isNative && presentation === "fullscreen") {
    return (
      <DialogPortal>
        <DialogOverlay
          className={cn("z-[269] bg-black/95", enterStable)}
          {...(!nestedUndimmed ? { onPointerDown: sheetSwipe.backdropPointerDown } : {})}
        />
        <DialogPrimitive.Content
          ref={sheetSwipe.mergeSheetRef(ref)}
          className={cn(
            "fixed inset-0 flex h-dvh w-dvh max-h-none max-w-none flex-col",
            nativeMediaViewerZ,
            "m-0 gap-0 overflow-hidden rounded-none border-none p-0 shadow-none",
            "bg-transparent pointer-events-auto native-bottom-sheet",
            enterStable,
            className,
          )}
          onPointerDownOutside={sheetSwipe.blockRadixDismiss}
          onInteractOutside={sheetSwipe.blockRadixDismiss}
          {...props}
        >
          {sheetSwipe.dragHandle}
          {sheetSwipe.fullscreenDismissZone}
          {children}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  }

  if (isNative) {
    const overlayDismiss =
      !nestedUndimmed ? { onPointerDown: sheetSwipe.backdropPointerDown } : {};
    return (
      <DialogPortal>
        <DialogOverlay
          className={cn(
            nestedOverlayClass,
            enterStable,
            "native-overlay-stable",
          )}
          {...overlayDismiss}
        />
        <DialogPrimitive.Content
          ref={sheetSwipe.mergeSheetRef(ref)}
          className={cn(
            `fixed inset-x-0 bottom-0 top-auto ${nestedSheetZ} w-full max-w-none mx-0 rounded-t-2xl`,
            effectiveNested && "native-nested-sheet",
            nestedUndimmed && "native-nested-undimmed",
            "native-bottom-sheet gap-0 border bg-background p-0 shadow-lg",
            "rounded-t-2xl rounded-b-none overflow-hidden flex flex-col",
            enterStable,
            effectiveExpandable && "native-sheet-expandable",
            effectiveCompact && "native-sheet-compact",
            "pointer-events-auto will-change-transform",
            nativeSheetBottomPad,
            className,
            nativeSheetLayoutLock,
          )}
          onPointerDownOutside={sheetSwipe.blockRadixDismiss}
          onInteractOutside={sheetSwipe.blockRadixDismiss}
          onEscapeKeyDown={(e) => {
            e.preventDefault();
            dismissCtx?.requestClose();
          }}
          {...props}
        >
          {sheetSwipe.dragHandle}
          <div
            className={cn(
              "native-sheet-body overflow-y-auto overflow-x-hidden flex-1 min-h-0 overscroll-contain px-4 pb-4",
              "pb-[calc(1rem+12px+env(safe-area-inset-bottom,0px))]",
              nativeSheetScrollBottomPad,
            )}
          >
            {children}
          </div>
          {showCloseButton && (
            <button
              type="button"
              className="absolute right-3 top-3 rounded-full opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus-visible:outline-none z-[101] h-9 w-9 flex items-center justify-center bg-background/90 backdrop-blur-sm shadow-sm"
              aria-label="Close"
              onClick={() => dismissCtx?.requestClose()}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  }

  return (
    <DialogPortal>
      <DialogOverlay
        className={webNestedStack ? "z-[230]" : "!z-[220]"}
      />
      <div
        className={cn(
          "fixed inset-0 h-dvh w-dvh flex items-center justify-center pointer-events-none overflow-hidden px-[2px] sm:px-0",
          webNestedStack ? "z-[230]" : "z-[220]",
        )}
      >
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            "relative w-full max-w-lg mx-auto",
            webNestedStack ? "z-[231]" : "z-[221]",
            "gap-4 border bg-background p-4 sm:p-6 shadow-lg duration-200",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
            "rounded-xl sm:rounded-2xl overflow-hidden flex flex-col",
            "safe-area-inset pointer-events-auto",
            className,
            "top-auto right-auto bottom-auto left-auto translate-x-0 translate-y-0",
            "sm:top-auto sm:right-auto sm:bottom-auto sm:left-auto sm:translate-x-0 sm:translate-y-0",
            "h-auto max-h-[80dvh] sm:max-h-[85dvh]",
          )}
          {...props}
        >
          <div className="overflow-y-auto overflow-x-hidden flex-1 min-h-0 overscroll-contain">
            {children}
          </div>
          {showCloseButton && (
            <DialogPrimitive.Close
              className="absolute right-2 top-2 sm:right-4 sm:top-4 rounded-full opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus-visible:outline-none disabled:pointer-events-none z-[101] h-9 w-9 sm:h-8 sm:w-8 flex items-center justify-center bg-background/90 backdrop-blur-sm pointer-events-auto shrink-0 shadow-sm touch-manipulation"
              aria-label="Close"
            >
              <X className="h-4 w-4 sm:h-5 sm:w-5" />
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </div>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
